//Attachment from tenant side
import React, { useState, useRef } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
    Button,
    Paper,
    Grid
} from '@mui/material';
import {
    Image as ImageIcon,
    FileText as FileTextIcon,
    Download as DownloadIcon,
    MoreVertical as MoreVerticalIcon,
    Trash2 as TrashIcon,
    AlertCircle as AlertCircleIcon,
    File as FileIcon,
    Upload as UploadIcon,
    Eye as EyeIcon,
    Paperclip as PaperclipIcon
} from 'react-feather';

// Helper function to determine file type icon
const getFileIcon = (fileName) => {
    if (!fileName) return <FileIcon size={24} color="#9CA3AF" />;

    const extension = fileName.split('.').pop().toLowerCase();

    // Image types
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(extension)) {
        return <ImageIcon size={24} color="#9CA3AF" />;
    }

    // Document types
    if (['pdf', 'doc', 'docx', 'txt', 'rtf', 'xls', 'xlsx', 'ppt', 'pptx'].includes(extension)) {
        return <FileTextIcon size={24} color="#9CA3AF" />;
    }

    // Default file icon
    return <FileIcon size={24} color="#9CA3AF" />;
};

// Helper function to format file size
const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Format date helper function
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (error) {
        console.error("Date formatting error:", error);
        return 'Invalid date';
    }
};

// Determine if file is viewable in preview
const isFileViewable = (fileName) => {
    if (!fileName) return false;
    const extension = fileName.split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'pdf'].includes(extension);
};

const TenantAttachmentsSection = ({
                                      attachments = [],
                                      onUpload,
                                      onDownload,
                                      onDelete,
                                      isLoading = false,
                                      allowUpload = true
                                  }) => {
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedAttachment, setSelectedAttachment] = useState(null);
    const [loadingStates, setLoadingStates] = useState({});
    const [errorStates, setErrorStates] = useState({});
    const [uploadLoading, setUploadLoading] = useState(false);
    const fileInputRef = useRef(null);

    const handleMenuOpen = (event, attachment) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
        setSelectedAttachment(attachment);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setSelectedAttachment(null);
    };

    const handleDownload = (event, attachment = selectedAttachment) => {
        if (event) event.stopPropagation();
        if (onDownload && attachment) {
            onDownload(attachment);
        }
        handleMenuClose();
    };

    const handleDelete = (event) => {
        event.stopPropagation();
        if (onDelete && selectedAttachment) {
            onDelete(selectedAttachment);
        }
        handleMenuClose();
    };

    const handleImageLoad = (id) => {
        setLoadingStates(prev => ({
            ...prev,
            [id]: false
        }));
    };

    const handleImageError = (id) => {
        setLoadingStates(prev => ({
            ...prev,
            [id]: false
        }));
        setErrorStates(prev => ({
            ...prev,
            [id]: true
        }));
    };

    // Trigger file input click
    const triggerFileUpload = () => {
        fileInputRef.current?.click();
    };

    // Handle file upload
    const handleFileUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            setUploadLoading(true);
            const success = await onUpload(file);

            if (!success) {
                throw new Error("Upload failed");
            }
        } catch (error) {
            console.error("Error handling file upload:", error);
        } finally {
            setUploadLoading(false);
            // Reset input to allow uploading the same file again
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // Preview attachment (similar to landlord-side)
    const previewAttachment = (attachment) => {
        if (!attachment.base64 && !attachment.thumbnail) {
            console.error("No preview data available for this attachment");
            return;
        }

        try {
            const fileName = attachment.fileName || attachment.file_name;
            const fileType = attachment.fileType || attachment.file_type;
            const previewData = attachment.base64 || attachment.thumbnail;

            // Determine if this is an image type
            const isImage = fileType?.startsWith('image/') ||
                ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(
                    fileName?.split('.').pop().toLowerCase()
                );

            if (isImage) {
                // Open image in new tab
                const newTab = window.open();
                newTab.document.write(`
                    <html>
                        <head>
                            <title>${fileName || 'Image Preview'}</title>
                            <style>body { margin: 0; display: flex; justify-content: center; align-items: center; height: 100vh; background: #f0f0f0; }</style>
                        </head>
                        <body>
                            <img src="data:${fileType || 'image/jpeg'};base64,${previewData}" 
                                style="max-width: 95%; max-height: 95%; object-fit: contain;" />
                        </body>
                    </html>
                `);
                newTab.document.close();
            } else if (fileType === 'application/pdf' ||
                fileName?.toLowerCase().endsWith('.pdf')) {
                // Handle PDF preview
                const newTab = window.open();
                newTab.document.write(`
                    <html>
                        <head>
                            <title>${fileName || 'PDF Preview'}</title>
                            <style>body, html { margin: 0; height: 100%; }</style>
                        </head>
                        <body>
                            <embed src="data:application/pdf;base64,${previewData}" 
                                type="application/pdf" width="100%" height="100%" />
                        </body>
                    </html>
                `);
                newTab.document.close();
            } else {
                // For other file types, just download
                handleDownload(null, attachment);
            }
        } catch (error) {
            console.error("Error previewing attachment:", error);
        }
    };

    const handleAttachmentClick = (attachment) => {
        if (isFileViewable(attachment.fileName || attachment.file_name)) {
            previewAttachment(attachment);
        } else {
            handleDownload(null, attachment);
        }
    };

    return (
        <>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1
            }}>
                <Typography variant="subtitle2" sx={{
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <PaperclipIcon size={16} />
                    Attachments ({attachments?.length || 0})
                </Typography>

                {allowUpload && (
                    <Button
                        size="small"
                        startIcon={uploadLoading ? <CircularProgress size={16} /> : <UploadIcon size={16} />}
                        onClick={triggerFileUpload}
                        disabled={uploadLoading}
                    >
                        {uploadLoading ? "Uploading..." : "Upload File"}
                    </Button>
                )}

                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                />
            </Box>

            {isLoading ? (
                <Paper
                    elevation={0}
                    sx={{
                        p: 4,
                        mb: 3,
                        backgroundColor: '#F9FAFB',
                        borderRadius: '8px',
                        display: 'flex',
                        justifyContent: 'center'
                    }}
                >
                    <CircularProgress size={24} />
                </Paper>
            ) : !attachments || attachments.length === 0 ? (
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mb: 3,
                        backgroundColor: '#F9FAFB',
                        borderRadius: '8px',
                        textAlign: 'center'
                    }}
                >
                    <Typography variant="body2" color="textSecondary">No attachments</Typography>
                </Paper>
            ) : (
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mb: 3,
                        backgroundColor: '#F9FAFB',
                        borderRadius: '8px'
                    }}
                >
                    <Grid container spacing={2}>
                        {attachments.map((attachment, index) => {
                            const attachmentId = attachment.id || attachment.attachment_id || index;
                            const fileName = attachment.fileName || attachment.file_name || `Attachment ${index + 1}`;
                            const fileSize = attachment.fileSize || attachment.file_size;
                            const uploadDate = attachment.date_created || attachment.uploadedAt || attachment.dateCreated;
                            const previewData = attachment.base64 || attachment.thumbnail;
                            const fileType = attachment.fileType || attachment.file_type;

                            // Auto set loading state for images
                            if (loadingStates[attachmentId] === undefined && previewData &&
                                (fileType?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(
                                    fileName.split('.').pop().toLowerCase()
                                ))) {
                                loadingStates[attachmentId] = true;
                            }

                            return (
                                <Grid item key={attachmentId} xs={12} sm={6} md={4}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            p: 0,
                                            border: '1px solid',
                                            borderColor: 'rgba(0,0,0,0.12)',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                                            }
                                        }}
                                        onClick={() => handleAttachmentClick(attachment)}
                                    >
                                        {/* Thumbnail/Preview Area */}
                                        <Box sx={{
                                            height: '120px',
                                            backgroundColor: '#F3F4F6',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative'
                                        }}>
                                            {/* Menu Button (Top Right) */}
                                            <Box sx={{
                                                position: 'absolute',
                                                top: 4,
                                                right: 4,
                                                zIndex: 2,
                                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                                borderRadius: '50%'
                                            }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleMenuOpen(e, attachment)}
                                                    aria-label="Attachment options"
                                                >
                                                    <MoreVerticalIcon size={16} />
                                                </IconButton>
                                            </Box>

                                            {previewData && !errorStates[attachmentId] &&
                                            (fileType?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(
                                                fileName.split('.').pop().toLowerCase()
                                            )) ? (
                                                <>
                                                    {loadingStates[attachmentId] && (
                                                        <Box sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            backgroundColor: 'rgba(243, 244, 246, 0.7)',
                                                            zIndex: 1
                                                        }}>
                                                            <CircularProgress size={20} />
                                                        </Box>
                                                    )}
                                                    <img
                                                        src={`data:image/jpeg;base64,${previewData}`}
                                                        alt={fileName}
                                                        style={{
                                                            width: '100%',
                                                            height: '120px',
                                                            objectFit: 'cover'
                                                        }}
                                                        onLoad={() => handleImageLoad(attachmentId)}
                                                        onError={() => handleImageError(attachmentId)}
                                                    />
                                                </>
                                            ) : errorStates[attachmentId] ? (
                                                <Tooltip title="Failed to load preview">
                                                    <AlertCircleIcon size={36} color="#EF4444" />
                                                </Tooltip>
                                            ) : (
                                                getFileIcon(fileName)
                                            )}
                                        </Box>

                                        {/* File Info Area */}
                                        <Box sx={{
                                            p: 1.5,
                                            backgroundColor: 'white'
                                        }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 500,
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 1,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {fileName}
                                            </Typography>

                                            <Box sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                mt: 0.5
                                            }}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {fileSize ? formatFileSize(fileSize) : ''}
                                                </Typography>

                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {uploadDate ? formatDate(uploadDate) : ''}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Action Buttons */}
                                        <Box sx={{
                                            display: 'flex',
                                            borderTop: '1px solid rgba(0,0,0,0.08)',
                                        }}>
                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    p: 0.75,
                                                    cursor: 'pointer',
                                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    previewAttachment(attachment);
                                                }}
                                            >
                                                <Tooltip title="Preview">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <EyeIcon size={14} />
                                                        <Typography variant="caption">View</Typography>
                                                    </Box>
                                                </Tooltip>
                                            </Box>

                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    p: 0.75,
                                                    borderLeft: '1px solid rgba(0,0,0,0.08)',
                                                    cursor: 'pointer',
                                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDownload(e, attachment);
                                                }}
                                            >
                                                <Tooltip title="Download">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <DownloadIcon size={14} />
                                                        <Typography variant="caption">Download</Typography>
                                                    </Box>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Paper>
            )}

            {/* Options Menu */}
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                onClick={(e) => e.stopPropagation()}
            >
                <MenuItem onClick={(e) => handleDownload(e)}>
                    <DownloadIcon size={16} style={{ marginRight: 8 }} />
                    Download
                </MenuItem>
                {onDelete && (
                    <MenuItem onClick={handleDelete} sx={{ color: '#EF4444' }}>
                        <TrashIcon size={16} style={{ marginRight: 8 }} />
                        Delete
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};

export default TenantAttachmentsSection;